/**
 * Stored timestamps as the desk reads them: on the New York clock.
 *
 * Timestamps are stored as instants, and every naive one on the platform is
 * UTC (SQLite hands back `DateTime(timezone=True)` columns without a zone —
 * `backend/app/core/desk_calendar.py`). The Created column used to read an
 * instant through the browser's own zone, so the same deal printed one hour on
 * a New York screen, another on a laptop elsewhere, and a third in a scheduled
 * report built on the server. One zone — the desk's — makes the screen, the
 * workbook and the schedule say the same thing. The server twin is
 * `backend/app/reporting/view_grid._desk_stamp`; the parity fixture holds the
 * two to the same text.
 */
import { DESK_TIME_ZONE } from '@/utils/dates'

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/
const ZONED_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/

// `hourCycle: 'h23'`, not `hour12: false`: the latter prints midnight as "24".
const DESK_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: DESK_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/** The instant a stored timestamp names; a naive one is UTC. Null when the text is not a timestamp. */
function instantOf(raw: string): Date | null {
  const text = raw.trim()
  if (!DATE_TIME_RE.test(text)) return null
  const iso = text.replace(' ', 'T')
  const when = new Date(ZONED_RE.test(iso) ? iso : `${iso}Z`)
  return Number.isNaN(when.getTime()) ? null : when
}

function deskParts(when: Date): Record<string, string> {
  const parts: Record<string, string> = {}
  for (const part of DESK_PARTS.formatToParts(when)) parts[part.type] = part.value
  return parts
}

/** "YYYY-MM-DD HH:MM" on the desk's clock for a stored timestamp, or null when the text is not one. */
export function deskStamp(raw: string): string | null {
  const when = instantOf(raw)
  if (!when) return null
  const p = deskParts(when)
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`
}

/**
 * The New York calendar day of a stored value, as "YYYY-MM-DD", or null.
 *
 * A bare date is a calendar day already and is returned as written — shifting
 * it through a zone would move it a day. A timestamp is the day the desk saw
 * it, which is not the UTC day for anything after 8pm Eastern.
 */
export function deskDateIso(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null
  const text = String(raw).trim()
  if (!text) return null
  if (DATE_ONLY_RE.test(text)) return text
  const when = instantOf(text)
  if (!when) return null
  const p = deskParts(when)
  return `${p.year}-${p.month}-${p.day}`
}
